import { useEffect } from 'react';
import 'twin.macro';
import { useInfiniteQuery } from '@tanstack/react-query';

import { SegmentCreatorUIBuilder } from './SegmentCreatorUIBuilder';
import { AQLQuery, AQLQueryType } from '@insights_module/adc_ql/types';
import { genRandomString } from '@utils/commonUtils';
import {
  ROOT_SEGMENT_ID,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { ISearchAudioResponse } from '@insights_module/types/Episode';
import { adcApi } from '@configs/axios-instances';
import { ISearchGroupsResponse } from '@insights_module/types/Show';
import { ParamsType, getShowsQueryObject } from '@insights_module/utils/showsUtils';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
} from '@insights_module/adc_ql/queries';
import { useMainTableContext } from '@insights_module/context/MainTableContext';

const queryFunction = async ({
  type,
  pageParam = 1,
  pageSize = 0,
  query,
  includeUnprocessed = false,
}: {
  type: 'audio' | 'group';
  pageParam: number;
  pageSize?: number;
  query: AQLQuery | null;
  includeUnprocessed?: boolean;
}) => {
  let res = {} as ISearchAudioResponse | ISearchGroupsResponse;
  // temporally we check if all filters are removed, and if they are we add one that give us all processed episodes (but only if includeUnprocessed is false)
  if (
    !includeUnprocessed &&
    query &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ((query.all && query.all.length === 0) || (query.any && query.any.length === 0))
  ) {
    query = {
      all: [
        new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
            gte: 'unrelated',
            lte: 'floor',
          }),
        ]),
      ],
    } as unknown as AQLQuery;
  }

  if (type === 'audio') {
    res = await adcApi.POST.post(`/audio/search?page_no=${pageParam}&page_size=${pageSize}`, {
      query,
    });
  }

  if (type === 'group') {
    res = await adcApi.POST.post(`/groups/search?page_no=${pageParam}&page_size=${pageSize}`, {
      ...getShowsQueryObject(query as ParamsType, includeUnprocessed),
    });
  }

  const { data, pagination } = res;
  return { data, pagination, pageParam };
};

export const SegmentCreator = () => {
  const { queryToken } = useInsightsParams();
  const {
    segmentsData,
    query,
    pushSegmentData,
    setTotalEpisodeCount,
    setIsTotalEpisodesLoading,
    queryType,
  } = useSegmentCreatorContext();
  const { includeUnprocessed } = useMainTableContext();

  function addSegment(groupId: string | null = null, type: AQLQueryType, id?: string): string {
    const newId = genRandomString(16);
    pushSegmentData({ id: id || newId, groupId, type, negation: false });

    return newId;
  }

  useEffect(() => {
    if (segmentsData.length === 0 && !queryToken) {
      addSegment(null, 'all', ROOT_SEGMENT_ID);
    }
  }, []);

  const { isFetching } = useInfiniteQuery({
    queryKey: ['segment-creator', JSON.stringify(query)],
    queryFn: () =>
      queryFunction({
        type: queryType,
        pageParam: 1,
        pageSize: 1,
        query,
        includeUnprocessed,
      }),
    onSuccess: (data) => {
      const total = data?.pages[0]?.pagination?.total;
      setTotalEpisodeCount(total);
    },
    enabled: !!query,
    cacheTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    setIsTotalEpisodesLoading(isFetching);
  }, [isFetching]);

  return (
    <div tw='flex flex-col pt-4 w-full'>
      <SegmentCreatorUIBuilder />
    </div>
  );
};
